
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DatabaseProvider } from './context/DatabaseContext';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import BooksPage from './pages/public/BooksPage';
import BookDetailPage from './pages/public/BookDetailPage';
import BoardPage from './pages/public/BoardPage';
import ContactPage from './pages/public/ContactPage';
import BranchesPage from './pages/public/BranchesPage';
import BranchDetail from './pages/public/BranchDetail';
import EventsPage from './pages/public/EventsPage';
import EventDetail from './pages/public/EventDetailPage';
import QanoonPage from './pages/public/QanoonPage';
import LoginPage from './pages/public/LoginPage';
import RegisterPage from './pages/public/RegisterPage';
import BoardMemberDetail from './pages/public/BoardMemberDetail';

import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import MemberDashboard from './pages/admin/MemberDashboard';
import ManageBranches from './pages/admin/ManageBranches';
import ManageMembers from './pages/admin/ManageMembers';
import ManageUsers from './pages/admin/ManageUsers';
import ManageEvents from './pages/admin/ManageEvents';
import ManageBooks from './pages/admin/ManageBooks';
import ManageBoard from './pages/admin/ManageBoard';
import ManageBranchRequests from './pages/admin/ManageBranchRequests';
import ManageTransfers from './pages/admin/ManageTransfers';
import ManageDistricts from './pages/admin/ManageDistricts';
import ManageLiteraryFields from './pages/admin/ManageLiteraryFields';
import ManageVotes from './pages/admin/ManageVotes';
import DataMigration from './pages/admin/DataMigration';
import SeedData from './pages/admin/SeedData';
import MyWritings from './pages/admin/MyWritings';


import './styles/global.css';

const PublicPageLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
  </>
);

const App = () => {
  return (
    <DatabaseProvider>
      <AuthProvider>
        <LanguageProvider>
          <Router>
            <ScrollToTop />
            <AppContent />
          </Router>
        </LanguageProvider>
      </AuthProvider>
    </DatabaseProvider>
  );
};

const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p>Loading application...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicPageLayout><HomePage /></PublicPageLayout>} />
      <Route path="/about" element={<PublicPageLayout><AboutPage /></PublicPageLayout>} />
      <Route path="/books" element={<PublicPageLayout><BooksPage /></PublicPageLayout>} />
      <Route path="/books/:id" element={<PublicPageLayout><BookDetailPage /></PublicPageLayout>} />
      <Route path="/board" element={<PublicPageLayout><BoardPage /></PublicPageLayout>} />
      <Route path="/board/:id" element={<PublicPageLayout><BoardMemberDetail /></PublicPageLayout>} />
      <Route path="/contact" element={<PublicPageLayout><ContactPage /></PublicPageLayout>} />
      <Route path="/branches" element={<PublicPageLayout><BranchesPage /></PublicPageLayout>} />
      <Route path="/branch/:id" element={<PublicPageLayout><BranchDetail /></PublicPageLayout>} />
      <Route path="/events" element={<PublicPageLayout><EventsPage /></PublicPageLayout>} />
      <Route path="/event/:id" element={<PublicPageLayout><EventDetail /></PublicPageLayout>} />
      <Route path="/events/:id" element={<PublicPageLayout><EventDetail /></PublicPageLayout>} />
      <Route path="/qanoon" element={<PublicPageLayout><QanoonPage /></PublicPageLayout>} />
      
      {/* Standalone pages */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={user && ['main_admin', 'branch_admin', 'center_body_secretary_general', 'center_body_joint_secretary', 'center_body_treasurer', 'center_body_auditor', 'center_body_woman_joint_secretary', 'district_coordinator', 'branch_secretary_general', 'branch_joint_secretary', 'branch_treasurer', 'branch_auditor', 'branch_counselor', 'member'].includes(user.role) ? <AdminLayout /> : <Navigate to="/login" />}
      >
        <Route index element={<Navigate to="/admin/dashboard" />} />
        <Route path="dashboard" element={(user?.role === 'main_admin' || user?.role?.startsWith('center_body_') || user?.role === 'branch_admin' || user?.role === 'branch_secretary_general') ? <Dashboard /> : <MemberDashboard />} />
        <Route path="branch-requests" element={<ManageBranchRequests />} />
        <Route path="transfers" element={<ManageTransfers />} />
        <Route path="branches" element={<ManageBranches />} />
        <Route path="districts" element={<ManageDistricts />} />
        <Route path="literary-fields" element={<ManageLiteraryFields />} />
        <Route path="members" element={<ManageMembers />} />
        <Route path="users" element={<ManageUsers />} />
        <Route path="books" element={<ManageBooks />} />
        <Route path="board" element={<ManageBoard />} />
        <Route path="events" element={<ManageEvents />} />
        <Route path="votes" element={<ManageVotes />} />
        <Route path="my-writings" element={['member', 'branch_auditor', 'branch_treasurer', 'branch_joint_secretary', 'branch_counselor'].includes(user?.role) ? <MyWritings /> : <Navigate to="/admin" />} />
        <Route path="migrate" element={<DataMigration />} />
        <Route path="seed-data" element={<SeedData />} />
      </Route>
    </Routes>
  );
};

export default App;
